import React, { useState, useEffect, useCallback } from 'react';
import Pagination from 'react-js-pagination';
import {
    Box,
    Button,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    Flex,
    VStack,
    HStack,
    Heading,
    Icon,
    Grid,
    GridItem,
    Tag,
    Spinner,
    useToast,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    ModalFooter,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    useDisclosure,
    Center
} from '@chakra-ui/react';
import { LuBrainCircuit, LuEye, LuFilter, LuPlay, LuSearch, LuTrendingUp } from 'react-icons/lu';
import Header from '../../../components/Header';
import API from '../../../helpers/axiosInstance';
import usePagePermissions from '../../../hooks/usePagePermissions';
import BoxWrapper from '../../../components/BoxWrapper/BoxWrapper';
import PageHeading from '../../../components/shared/PageHeading';


export default function SMSGrouping() {
    const [searchQuery, setSearchQuery] = useState('');
    const [eventFilter, setEventFilter] = useState('');
    const [eventOptions, setEventOptions] = useState([]);
    const [mode, setMode] = useState('monitoring');
    const [activeTab, setActiveTab] = useState(0); // 0 for No Template Found, 1 for Monitoring
    const [notFoundMessages, setNotFoundMessages] = useState([]);
    const [monitoringMessages, setMonitoringMessages] = useState([]);
    const [notFoundPage, setNotFoundPage] = useState(1);
    const [notFoundTotal, setNotFoundTotal] = useState(0);
    const [monitorPage, setMonitorPage] = useState(1);
    const [monitorTotal, setMonitorTotal] = useState(0);
    const PAGE_SIZE = 50;
    const [stats, setStats] = useState({ totalMessage: 0, unique_count: 0, mode: 0 });
    const [loading, setLoading] = useState(false);
    const [modeLoading, setModeLoading] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const { loading: permissionsLoading, can } = usePagePermissions('tools/sms-group');

    const listPermission = can("sms-group-list")
    const modePermission = can("sms-group-change-mode")
    const getApiHeaders = () => ({
        "x-api-key": localStorage.getItem("api-key"),
        "x-access-token": localStorage.getItem("customer-portal"),
    });

    // Fetch stats
    const fetchStats = async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/template/unmatch/stat`;
            const response = await API.get(url, { headers: getApiHeaders() });
            setStats(response.data.data || { totalMessage: 0, unique_count: 0, mode: 0 });
            setMode(response.data.data.mode === 1 ? 'live' : 'monitoring');
        } catch (error) {
            console.error("Error fetching stats:", error);
            toast({
                title: "Error",
                description: "Failed to fetch statistics",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    // Fetch distinct events for the Event filter dropdown
    const fetchEventList = async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/template/unmatch/event-list`;
            const response = await API.get(url, { headers: getApiHeaders() });
            setEventOptions(response.data.data || []);
        } catch (error) {
            console.error("Error fetching event list:", error);
        }
    };

    // Fetch Not Found List
    const fetchNotFoundList = async (search = '', page = 1, eventName = '') => {
        setLoading(true);
        try {
            const qs = new URLSearchParams({ page, limit: PAGE_SIZE });
            if (search) qs.set('search', search);
            if (eventName) qs.set('event_name', eventName);
            const url = `${process.env.REACT_APP_API_URL}/template/unmatch/not-found-list?${qs.toString()}`;
            const response = await API.get(url, { headers: getApiHeaders() });
            const data = response.data.data || [];
            setNotFoundTotal(response.data.total ?? data.length);

            // Map API response to component format
            const mappedData = data.map(item => ({
                id: item.id,
                sampleMessage: item.raw_text,
                templateSuggestion: item.template_suggestion || '',
                eventName: item.event_name || '',
                eventPayload: item.event_payload || '',
                pattern: item.raw_text,
                count: item.count,
                lastReceived: new Date(item.updated_at).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                }),
                category: item.TemplateMessage?.message_type_id === 1 ? 'OTP' : 'General'
            }));

            setNotFoundMessages(mappedData);
        } catch (error) {
            console.error("Error fetching not found list:", error);
            toast({
                title: "Error",
                description: "Failed to fetch messages",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch Monitor List
    const fetchMonitorList = async (search = '', page = 1) => {
        setLoading(true);
        try {
            const qs = new URLSearchParams({ page, limit: PAGE_SIZE });
            if (search) qs.set('search', search);
            const url = `${process.env.REACT_APP_API_URL}/template/unmatch/monitor-list?${qs.toString()}`;
            const response = await API.get(url, { headers: getApiHeaders() });
            const data = response.data.data || [];
            setMonitorTotal(response.data.total ?? data.length);

            // Map API response to component format
            const mappedData = data.map(item => ({
                id: item.id,
                sampleMessage: item.raw_text,
                templateName: item.TemplateMessage?.name || 'Unknown',
                templateMessage: item.TemplateMessage?.message || '',
                pattern: item.TemplateMessage?.message || item.raw_text,
                count: item.count,
                lastReceived: new Date(item.updated_at).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                }),
                category: item.TemplateMessage?.message_type_id === 1 ? 'OTP' : 'General'
            }));

            setMonitoringMessages(mappedData);
        } catch (error) {
            console.error("Error fetching monitor list:", error);
            toast({
                title: "Error",
                description: "Failed to fetch monitoring messages",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    // Change Mode
    const changeMode = async (newMode) => {
        setModeLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/template/unmatch/change-mode`;
            const payload = { mode: newMode === 'live' ? 1 : 0 };
            await API.post(url, payload, { headers: getApiHeaders() });
            setMode(newMode);
            toast({
                title: "Success",
                description: `Mode changed to ${newMode}`,
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error("Error changing mode:", error);
            toast({
                title: "Error",
                description: "Failed to change mode",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setModeLoading(false);
        }
    };

    // Debounce function
    const debounce = (func, delay) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                func(...args);
            }, delay);
        };
    };

    // Debounced search for Not Found List — resets to page 1
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearchNotFound = useCallback(
        debounce((search, eventName) => {
            setNotFoundPage(1);
            fetchNotFoundList(search, 1, eventName);
        }, 500),
        []
    );

    // Debounced search for Monitor List — resets to page 1
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearchMonitor = useCallback(
        debounce((search) => {
            setMonitorPage(1);
            fetchMonitorList(search, 1);
        }, 500),
        []
    );

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);

        // Call appropriate debounced search based on current tab
        if (activeTab === 0) {
            debouncedSearchNotFound(value, eventFilter);
        } else {
            debouncedSearchMonitor(value);
        }
    };

    // Handle Event filter change (Template Discovery tab)
    const handleEventFilterChange = (e) => {
        const value = e.target.value;
        setEventFilter(value);
        setNotFoundPage(1);
        fetchNotFoundList(searchQuery, 1, value);
    };

    // Handle tab change
    const handleTabChange = (index) => {
        setActiveTab(index);
        setSearchQuery(''); // Reset search when switching tabs
        setEventFilter(''); // Reset event filter when switching tabs

        // Fetch appropriate data
        if (index === 0) {
            setNotFoundPage(1);
            fetchNotFoundList('', 1, '');
        } else {
            setMonitorPage(1);
            fetchMonitorList('', 1);
        }
    };

    // Pagination handlers
    const handleNotFoundPageChange = (page) => {
        setNotFoundPage(page);
        fetchNotFoundList(searchQuery, page, eventFilter);
    };
    const handleMonitorPageChange = (page) => {
        setMonitorPage(page);
        fetchMonitorList(searchQuery, page);
    };

    // Handle message click to open modal
    const handleMessageClick = (message) => {
        setSelectedMessage(message);
        onOpen();
    };

    // Truncate text function
    const truncateText = (text, maxLength = 50) => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    // Initial data fetch
    useEffect(() => {
        if (permissionsLoading || !listPermission) return;
        fetchStats();
        fetchEventList();
        fetchNotFoundList('', 1);
        fetchMonitorList('', 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [permissionsLoading, listPermission]);

    // Refetch data when mode changes — back to page 1
    useEffect(() => {
        if (permissionsLoading || !listPermission) return;
        if (activeTab === 0) {
            setNotFoundPage(1);
            fetchNotFoundList(searchQuery, 1, eventFilter);
        } else {
            setMonitorPage(1);
            fetchMonitorList(searchQuery, 1);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const currentMessages = activeTab === 0 ? notFoundMessages : monitoringMessages;


    if (permissionsLoading) {
        return (
            <>
                <Header />
                <BoxWrapper>
                    <Center h="400px">
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                    </Center>
                </BoxWrapper>
            </>
        );
    }

    if (!listPermission) {
        return (
            <>
                <Header />
                <BoxWrapper>
                    <Center h="400px">
                        <Box textAlign="center">
                            <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                                No Access
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                                You don't have permission to view this sections.
                            </Text>
                        </Box>
                    </Center>
                </BoxWrapper>
            </>
        );
    }

    if (loading) {
        return (
            <>
                <Header />
                <BoxWrapper>
                    <Center h="400px">
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                    </Center>
                </BoxWrapper>
            </>
        );
    }
    return (
        <>
            <Header />
            <BoxWrapper>
                <PageHeading
                    icon={LuBrainCircuit}
                    title="Message Intelligence"
                    subtitle="Message Intelligence analyzes incoming SMS traffic in real time to detect message templates, group similar messages, and extract dynamic values. Once activated, it can seamlessly convert SMS into WhatsApp or other omni-channel messages without manual intervention."
                >
                    {/* Mode Selector */}
                    {modePermission && (<HStack spacing={3}>
                        <Text fontSize="xs" fontWeight="medium" color="gray.600">Mode:</Text>
                        <HStack spacing={2} p={1} bg="gray.100" borderRadius="lg">
                            <Button
                                size="sm"
                                bg={mode === 'monitoring' ? 'var(--brand-primary)' : 'transparent'}
                                color={mode === 'monitoring' ? 'white' : 'black'}
                                variant={mode === 'monitoring' ? 'solid' : 'ghost'}
                                onClick={() => changeMode('monitoring')}
                                leftIcon={<Icon as={LuEye} boxSize={4} />}
                                isLoading={modeLoading && mode !== 'monitoring'}
                                isDisabled={modeLoading}
                            >
                                Monitoring
                            </Button>
                            <Button
                                size="sm"
                                bg={mode === 'live' ? 'var(--brand-primary)' : 'transparent'}
                                color={mode === 'live' ? 'white' : 'black'}
                                variant={mode === 'live' ? 'solid' : 'ghost'}
                                onClick={() => changeMode('live')}
                                leftIcon={<Icon as={LuPlay} boxSize={4} />}
                                isLoading={modeLoading && mode !== 'live'}
                                isDisabled={modeLoading}
                            >
                                Live
                            </Button>
                        </HStack>
                    </HStack>)}
                </PageHeading>
                <Box >
                    <VStack spacing={6} align="stretch">
                        {/* Header Stats */}
                        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                            <GridItem>
                                <Box className='box-style-card'>
                                    <Box pb={2}>
                                        <Flex justify="space-between" align="center">
                                            <Heading size="sm" fontWeight={"500"}>Total Messages</Heading>
                                            <Icon as={LuTrendingUp} boxSize={4} color="gray.600" />
                                        </Flex>
                                    </Box>
                                    <Box pt={2}>
                                        <Text fontSize="2xl" fontWeight="semibold">
                                            {stats.total_message?.toLocaleString() || 0}
                                        </Text>
                                        <Text fontSize="xs" color="gray.600" mt={1}>Across all patterns</Text>
                                    </Box>
                                </Box>
                            </GridItem>

                            <GridItem>
                                <Box className='box-style-card'>
                                    <Box pb={2}>
                                        <Flex justify="space-between" align="center">
                                            <Heading size="sm" fontWeight={"500"}>Unique Patterns</Heading>
                                            <Icon as={LuFilter} boxSize={4} color="gray.600" />
                                        </Flex>
                                    </Box>
                                    <Box pt={2}>
                                        <Text fontSize="2xl" fontWeight="semibold">
                                            {stats.unique_count || 0}
                                        </Text>
                                        <Text fontSize="xs" color="gray.600" mt={1}>Detected patterns</Text>
                                    </Box>
                                </Box>
                            </GridItem>
                        </Grid>

                        {/* Main Content Card */}
                        <Box className='box-style-card'>
                            <Box>
                                <VStack spacing={4} align="stretch">

                                    {/* Mode Description */}


                                    <Flex
                                        p={3}
                                        borderRadius="lg"
                                        borderWidth="1px"
                                        bg={mode === 'monitoring' ? '#fff7ed' : '#f0fdf4'}
                                        borderColor={mode === 'monitoring' ? '#ffd7a8' : '#b9f8cf'}
                                        gap={2}
                                        align="flex-start"
                                    >
                                        <Box mt={0.5}>
                                            <Icon
                                                as={mode === 'monitoring' ? LuEye : LuPlay}
                                                boxSize={4}
                                                color={mode === 'monitoring' ? 'orange.600' : 'green.600'}
                                            />
                                        </Box>
                                        <Box flex={1}>
                                            <Text fontWeight="medium" color={mode === 'monitoring' ? '#7e2a0c' : 'green.900'}>
                                                {mode === 'monitoring' ? 'Monitoring Mode' : 'Live Mode'}
                                            </Text>
                                            <Text fontSize="xs" mt={0.5} color={mode === 'monitoring' ? 'orange.700' : 'green.700'}>
                                                {mode === 'monitoring'
                                                    ? 'Messages are analyzed to detect patterns and templates. No conversion or outbound messaging is performed.'
                                                    : 'Identified templates are activated. Messages are automatically converted and sent via WhatsApp or other configured channels.'
                                                }
                                            </Text>
                                        </Box>
                                        <Tag
                                            mt={0.5}
                                            bg={mode === 'monitoring' ? '#ffedd4' : '#dcfce7'}
                                            color={mode === 'monitoring' ? '#9f2d00' : '#016630'}
                                            fontSize="xs"
                                        >
                                            {mode === 'monitoring' ? 'Read Only' : 'Active'}
                                        </Tag>
                                    </Flex>

                                </VStack>
                            </Box>

                            <Box mt={6}>
                                {/* Filters */}
                                <Flex direction={{ base: 'column', md: 'row' }} gap={4} mb={6}>
                                    {/* Search */}
                                    <Box flex={1}>
                                        <InputGroup>
                                            <InputLeftElement pointerEvents="none">
                                                <Icon as={LuSearch} boxSize={4} color="gray.600" />
                                            </InputLeftElement>
                                            <Input
                                                placeholder="Search messages or patterns..."
                                                value={searchQuery}
                                                onChange={handleSearchChange}
                                            />
                                        </InputGroup>
                                    </Box>
                                    {/* Event filter (Template Discovery tab) */}
                                    {activeTab === 0 && (
                                        <Box w={{ base: '100%', md: '320px' }}>
                                            <Select
                                                value={eventFilter}
                                                onChange={handleEventFilterChange}
                                                placeholder="All events"
                                                icon={<LuFilter />}
                                            >
                                                {eventOptions.map((ev) => (
                                                    <option key={ev.event_name} value={ev.event_name}>
                                                        {ev.event_name} ({Number(ev.total).toLocaleString()})
                                                    </option>
                                                ))}
                                            </Select>
                                        </Box>
                                    )}
                                </Flex>

                                {/* Tabs */}
                                <Tabs index={activeTab} onChange={handleTabChange} colorScheme="blue">
                                    <TabList
                                        bg="#f1f5f9"
                                        p="1"
                                        borderRadius="full"
                                        display="flex"
                                        justifyContent="space-between"
                                        width={"40%"}
                                        mb={4}
                                    >
                                        <Tab flex="1" _selected={{ bg: "white", boxShadow: "sm", fontWeight: "600" }} borderRadius="full" fontSize="sm">Template Discovery</Tab>
                                        <Tab flex="1" _selected={{ bg: "white", boxShadow: "sm", fontWeight: "600" }} borderRadius="full" fontSize="sm">Template Detection</Tab>
                                    </TabList>

                                    <TabPanels>
                                        {/* No Template Found Tab */}
                                        <TabPanel p={0}>
                                            <Text fontSize="sm" color="gray.600" my={1.5}>
                                                Review messages and create templates to enable automatic detection.
                                            </Text>
                                            {loading ? (
                                                <Flex justify="center" align="center" minH="200px">
                                                    <Spinner size="xl" color="blue.500" />
                                                </Flex>
                                            ) : (
                                                <>
                                                    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflowX="auto">

                                                        <Table variant="simple" tableLayout="fixed">
                                                            <Thead>
                                                                <Tr>
                                                                    <Th w="240px">Sample Message</Th>
                                                                    <Th w="300px">Template Suggestion</Th>
                                                                    <Th w="300px">Suggested Event</Th>
                                                                    <Th w="90px">Count</Th>
                                                                    <Th w="110px">Last Received</Th>
                                                                </Tr>
                                                            </Thead>

                                                            <Tbody>
                                                                {notFoundMessages.length > 0 ? (
                                                                    notFoundMessages.map((message) => (
                                                                        <Tr key={message.id}>
                                                                            <Td
                                                                                color="gray.600"
                                                                                maxW="280px"
                                                                                whiteSpace="normal"
                                                                                wordBreak="break-word"
                                                                            >
                                                                                {message.sampleMessage}
                                                                            </Td>

                                                                            <Td
                                                                                fontFamily="mono"
                                                                                fontSize="xs"
                                                                                color="blue.700"
                                                                                maxW="300px"
                                                                                whiteSpace="normal"
                                                                                wordBreak="break-word"
                                                                            >
                                                                                {message.templateSuggestion || <Text as="span" color="gray.400" fontStyle="italic">—</Text>}
                                                                            </Td>

                                                                            <Td maxW="300px" whiteSpace="normal" wordBreak="break-word">
                                                                                {message.eventName ? (
                                                                                    <VStack align="stretch" spacing={1}>
                                                                                        <Tag size="sm" colorScheme="purple" fontFamily="mono" fontSize="xs" fontWeight="600" alignSelf="flex-start">
                                                                                            {message.eventName}
                                                                                        </Tag>
                                                                                        {message.eventPayload && (
                                                                                            <Box
                                                                                                as="pre"
                                                                                                fontFamily="mono"
                                                                                                fontSize="11px"
                                                                                                color="gray.600"
                                                                                                bg="gray.50"
                                                                                                borderRadius="md"
                                                                                                p={2}
                                                                                                whiteSpace="pre-wrap"
                                                                                                wordBreak="break-word"
                                                                                            >
                                                                                                {(() => {
                                                                                                    try {
                                                                                                        return JSON.stringify(JSON.parse(message.eventPayload), null, 2);
                                                                                                    } catch {
                                                                                                        return message.eventPayload;
                                                                                                    }
                                                                                                })()}
                                                                                            </Box>
                                                                                        )}
                                                                                    </VStack>
                                                                                ) : (
                                                                                    <Text as="span" color="gray.400" fontStyle="italic">—</Text>
                                                                                )}
                                                                            </Td>

                                                                            <Td>
                                                                                <Tag color="#1e293b" fontSize="xs" fontWeight="500" bg="#f2edee">
                                                                                    {message.count?.toLocaleString() || 0}
                                                                                </Tag>
                                                                            </Td>

                                                                            <Td color="gray.600">
                                                                                {message.lastReceived}
                                                                            </Td>
                                                                        </Tr>
                                                                    ))
                                                                ) : (
                                                                    <Tr>
                                                                        <Td colSpan={5} textAlign="center" color="gray.500">
                                                                            No messages found
                                                                        </Td>
                                                                    </Tr>
                                                                )}
                                                            </Tbody>
                                                        </Table>

                                                    </Box>

                                                    <Flex justify="space-between" align="center" mt={4} wrap="wrap" gap={2}>
                                                        <Text fontSize="xs" color="gray.600">
                                                            Showing {notFoundMessages.length} of {notFoundTotal.toLocaleString()} patterns
                                                            {notFoundTotal > PAGE_SIZE && ` · page ${notFoundPage} of ${Math.ceil(notFoundTotal / PAGE_SIZE)}`}
                                                        </Text>
                                                        {notFoundTotal > PAGE_SIZE && (
                                                            <Pagination
                                                                activePage={notFoundPage}
                                                                itemsCountPerPage={PAGE_SIZE}
                                                                totalItemsCount={notFoundTotal}
                                                                pageRangeDisplayed={5}
                                                                onChange={handleNotFoundPageChange}
                                                                innerClass="pagination"
                                                                itemClass="page-item"
                                                                activeLinkClass="page-link active"
                                                                linkClass="page-link"
                                                            />
                                                        )}
                                                    </Flex>
                                                </>
                                            )}
                                        </TabPanel>

                                        {/* Monitoring Tab */}
                                        <TabPanel p={0}>
                                            <Text fontSize="sm" color="gray.600" my={1.5}>
                                                SMS messages that have been successfully identified using existing templates.
                                            </Text>
                                            {loading ? (
                                                <Flex justify="center" align="center" minH="200px">
                                                    <Spinner size="xl" color="blue.500" />
                                                </Flex>
                                            ) : (
                                                <>
                                                    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflowX="auto">

                                                        <Table variant="simple">
                                                            <Thead>
                                                                <Tr>
                                                                    <Th>Sample Message</Th>
                                                                    <Th w="250px">Template Name</Th>
                                                                    <Th w="350px">Template Message</Th>
                                                                    <Th w="100px">Count</Th>
                                                                    <Th w="120px">Last Received</Th>
                                                                </Tr>
                                                            </Thead>

                                                            <Tbody>
                                                                {monitoringMessages.length > 0 ? (
                                                                    monitoringMessages.map((message) => (
                                                                        <Tr
                                                                            key={message.id}
                                                                            onClick={() => handleMessageClick(message)}
                                                                            cursor="pointer"
                                                                            _hover={{ bg: "gray.50" }}
                                                                        >
                                                                            {/* ✅ Wrapping text - no truncate */}
                                                                            <Td
                                                                                color="gray.600"
                                                                                maxW="300px"
                                                                                whiteSpace="normal"
                                                                                wordBreak="break-word"
                                                                            >
                                                                                {message.sampleMessage}
                                                                            </Td>

                                                                            {/* ✅ Non-wrapping tag */}
                                                                            <Td whiteSpace="nowrap">
                                                                                <Tag
                                                                                    bg="var(--brand-primary)"
                                                                                    color="white"
                                                                                    fontSize="xs"
                                                                                    fontWeight="semibold"
                                                                                >
                                                                                    {message.templateName}
                                                                                </Tag>
                                                                            </Td>

                                                                            {/* ✅ Wrapping text - no truncate */}
                                                                            <Td
                                                                                fontFamily="mono"
                                                                                fontSize="sm"
                                                                                maxW="350px"
                                                                                whiteSpace="normal"
                                                                                wordBreak="break-word"
                                                                            >
                                                                                {message.templateMessage}
                                                                            </Td>

                                                                            {/* ✅ Non-wrapping tag */}
                                                                            <Td whiteSpace="nowrap">
                                                                                <Tag
                                                                                    color="#1e293b"
                                                                                    fontSize="xs"
                                                                                    fontWeight="500"
                                                                                    bg="#f2edee"
                                                                                >
                                                                                    {message.count?.toLocaleString() || 0}
                                                                                </Tag>
                                                                            </Td>

                                                                            {/* ✅ Normal text */}
                                                                            <Td color="gray.600">
                                                                                {message.lastReceived}
                                                                            </Td>
                                                                        </Tr>
                                                                    ))
                                                                ) : (
                                                                    <Tr>
                                                                        <Td colSpan={5} textAlign="center" color="gray.500">
                                                                            No templates identified
                                                                        </Td>
                                                                    </Tr>
                                                                )}
                                                            </Tbody>
                                                        </Table>

                                                    </Box>

                                                    <Flex justify="space-between" align="center" mt={4} wrap="wrap" gap={2}>
                                                        <Text fontSize="xs" color="gray.600">
                                                            Showing {monitoringMessages.length} of {monitorTotal.toLocaleString()} templates identified
                                                            {monitorTotal > PAGE_SIZE && ` · page ${monitorPage} of ${Math.ceil(monitorTotal / PAGE_SIZE)}`}
                                                        </Text>
                                                        {monitorTotal > PAGE_SIZE && (
                                                            <Pagination
                                                                activePage={monitorPage}
                                                                itemsCountPerPage={PAGE_SIZE}
                                                                totalItemsCount={monitorTotal}
                                                                pageRangeDisplayed={5}
                                                                onChange={handleMonitorPageChange}
                                                                innerClass="pagination"
                                                                itemClass="page-item"
                                                                activeLinkClass="page-link active"
                                                                linkClass="page-link"
                                                            />
                                                        )}
                                                    </Flex>
                                                </>
                                            )}
                                        </TabPanel>
                                    </TabPanels>
                                </Tabs>
                            </Box>
                        </Box>
                    </VStack>
                </Box>

            </BoxWrapper>

            {/* Modal for Message Details */}
            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Message Details</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {selectedMessage && (
                            <VStack align="stretch" spacing={4}>
                                <Box>
                                    <Text fontWeight="semibold" fontSize="sm" color="gray.600" mb={2}>
                                        Sample Message:
                                    </Text>
                                    <Box
                                        p={3}
                                        bg="gray.50"
                                        borderRadius="md"
                                        borderWidth="1px"
                                        borderColor="gray.200"
                                    >
                                        <Text fontSize="sm">{selectedMessage.sampleMessage}</Text>
                                    </Box>
                                </Box>

                                {selectedMessage.templateName && (
                                    <Box>
                                        <Text fontWeight="semibold" fontSize="sm" color="gray.600" mb={2}>
                                            Template Name:
                                        </Text>
                                        <Tag colorScheme="blue" size="sm">
                                            {selectedMessage.templateName}
                                        </Tag>
                                    </Box>
                                )}

                                {selectedMessage.templateMessage && (
                                    <Box>
                                        <Text fontWeight="semibold" fontSize="sm" color="gray.600" mb={2}>
                                            Template Message:
                                        </Text>
                                        <Box
                                            p={3}
                                            bg="gray.50"
                                            borderRadius="md"
                                            borderWidth="1px"
                                            borderColor="gray.200"
                                            fontFamily="mono"
                                        >
                                            <Text fontSize="sm">{selectedMessage.templateMessage}</Text>
                                        </Box>
                                    </Box>
                                )}

                                {selectedMessage.pattern && !selectedMessage.templateMessage && (
                                    <Box>
                                        <Text fontWeight="semibold" fontSize="sm" color="gray.600" mb={2}>
                                            Pattern:
                                        </Text>
                                        <Box
                                            p={3}
                                            bg="gray.50"
                                            borderRadius="md"
                                            borderWidth="1px"
                                            borderColor="gray.200"
                                            fontFamily="mono"
                                        >
                                            <Text fontSize="sm">{selectedMessage.pattern}</Text>
                                        </Box>
                                    </Box>
                                )}

                                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                    <Box>
                                        <Text fontWeight="semibold" fontSize="sm" color="gray.600" mb={2}>
                                            Count:
                                        </Text>
                                        <Tag color="#1e293b" fontSize="sm" fontWeight="600" bg="#f2edee" p={2}>
                                            {selectedMessage.count?.toLocaleString() || 0}
                                        </Tag>
                                    </Box>

                                    <Box>
                                        <Text fontWeight="semibold" fontSize="sm" color="gray.600" mb={2}>
                                            Last Received:
                                        </Text>
                                        <Text fontSize="sm">{selectedMessage.lastReceived}</Text>
                                    </Box>
                                </Grid>

                                {selectedMessage.category && (
                                    <Box>
                                        <Text fontWeight="semibold" fontSize="sm" color="gray.600" mb={2}>
                                            Category:
                                        </Text>
                                        <Tag colorScheme="purple" size="sm" >
                                            {selectedMessage.category}
                                        </Tag>
                                    </Box>
                                )}
                            </VStack>
                        )}
                    </ModalBody>

                    <ModalFooter>
                        <Button colorScheme="blue" onClick={onClose}>
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}